import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import {
  checkRegister,
  register,
  type RegisterCheckResponse,
  type RegisterRequest,
} from '../api/auth';
import { ApiError } from '../api/client';

/**
 * Register page - three modes the server distinguishes via
 * /api/auth/register/check:
 *   - invitation     pre-fills email + organisation, locks email
 *   - initial_setup  no token, no super admin yet → first signup
 *                    becomes super admin
 *   - (403)          no token AND a super admin exists → bounce to
 *                    /login. The SPA handles this by surfacing the
 *                    error message and offering a "back to login"
 *                    link rather than auto-redirecting (less jarring).
 *
 * The check happens once on mount; the form doesn't render until
 * we know which mode we're in. This avoids the legacy template's
 * "show all variants" approach which made the markup messy.
 */
export function Register() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const checkQuery = useQuery({
    queryKey: ['register-check', token],
    queryFn: () => checkRegister(token),
    // Side-effect-free GET - fine to rerun on focus, but keep a
    // generous staleTime since the answer doesn't change while the
    // user fills in the form.
    staleTime: 60_000,
    retry: false,
  });

  if (checkQuery.isLoading) {
    return (
      <AuthShell>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)' }}>
          Checking invitation…
        </p>
      </AuthShell>
    );
  }

  if (checkQuery.isError) {
    const err = checkQuery.error;
    const message =
      err instanceof ApiError
        ? err.message
        : 'Something went wrong. Please try again.';
    return (
      <AuthShell
        title="Can't continue"
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <div className="auth-alert auth-alert-danger" role="alert">
          {message}
        </div>
      </AuthShell>
    );
  }

  return (
    <RegisterForm token={token} check={checkQuery.data!} />
  );
}

interface RegisterFormProps {
  token: string;
  check: RegisterCheckResponse;
}

function RegisterForm({ token, check }: RegisterFormProps) {
  const isInitialSetup = check.mode === 'initial_setup';
  const invitedEmail = check.invitation?.email ?? '';
  const clientName = check.client?.display_name ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(invitedEmail);
  const [phone, setPhone] = useState('');
  const [organisation, setOrganisation] = useState(clientName);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-sync if the check resolves AFTER the form mounted (StrictMode
  // double-render shouldn't hit this in practice, but it's cheap
  // insurance against a slow check coming in late).
  useEffect(() => {
    if (invitedEmail) setEmail(invitedEmail);
  }, [invitedEmail]);
  useEffect(() => {
    if (clientName) setOrganisation(clientName);
  }, [clientName]);

  const mutation = useMutation({
    mutationFn: (req: RegisterRequest) => register(req),
    onSuccess: (res) => {
      window.location.assign(res.redirect_to);
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);

    // Client-side validation - server re-checks but bailing early
    // saves a round-trip on the obvious cases.
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }

    mutation.mutate({
      token: token || undefined,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim().toLowerCase(),
      phone_number: phone.trim(),
      organisation: organisation.trim(),
      password,
    });
  }

  // ── Title / subtitle vary per mode ─────────────────────────────
  const title = isInitialSetup
    ? 'Create your super admin account'
    : check.client
      ? `Join ${check.client.display_name}`
      : 'Create your account';
  const subtitle = isInitialSetup
    ? 'You are setting up Locksmith for the first time.'
    : check.invitation
      ? check.invitation.role === 'admin'
        ? "You've been invited as an administrator."
        : "You've been invited as a user."
      : undefined;
  const submitLabel = mutation.isPending
    ? 'Creating account…'
    : isInitialSetup
      ? 'Create super admin'
      : 'Create account';

  return (
    <AuthShell
      title={title}
      subtitle={subtitle}
      footer={
        <p>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      }
    >
      {errorMessage && (
        <div className="auth-alert auth-alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <input
            className="auth-input"
            type="text"
            name="first_name"
            autoComplete="given-name"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={mutation.isPending}
            required
          />
          <input
            className="auth-input"
            type="text"
            name="last_name"
            autoComplete="family-name"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={mutation.isPending}
            required
          />
        </div>

        <input
          className="auth-input"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending || !!invitedEmail}
          readOnly={!!invitedEmail}
          required
        />
        {invitedEmail && (
          <p
            style={{
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-dim)',
              marginTop: 'calc(-1 * var(--space-2))',
            }}
          >
            Email must match your invitation.
          </p>
        )}

        <input
          className="auth-input"
          type="tel"
          name="phone_number"
          autoComplete="tel"
          placeholder="Phone number (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={mutation.isPending}
        />

        <input
          className="auth-input"
          type="text"
          name="organisation"
          autoComplete="organization"
          placeholder="Organisation"
          value={organisation}
          onChange={(e) => setOrganisation(e.target.value)}
          disabled={mutation.isPending || !!clientName}
          readOnly={!!clientName}
        />

        <input
          className="auth-input"
          type="password"
          name="password"
          autoComplete="new-password"
          placeholder="Password (min 8 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={mutation.isPending}
          minLength={8}
          required
        />
        <input
          className="auth-input"
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={mutation.isPending}
          minLength={8}
          required
        />

        <button
          type="submit"
          className="auth-btn"
          disabled={
            mutation.isPending ||
            !firstName ||
            !lastName ||
            !email ||
            !password ||
            !confirmPassword
          }
        >
          {submitLabel}
        </button>
      </form>
    </AuthShell>
  );
}
